/**
 * Service Categories Management Page
 * - Normalizes incoming category data so `id` and `parentId` are strings (frontend)
 * - Sends numeric parentId to API when creating/updating
 * - Fixed all TypeScript type-mismatch issues
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Filter,
  Upload,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Listbox } from '@headlessui/react';
import { useToastNotifications } from '../hooks/useToast';
import { realAPI } from '../api/realEndpoints';
import { formatDate } from '../utils/helpers';
import type { Category, CategoryFormData } from '../api/adminTypes';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Input from '../components/Input';
// Assuming the utility file is at '../utils/imageUpload'
import { uploadImage, validateImageFile } from '../utils/imageUpload';
import './ContentManagement.css';
const IMAGE_URL = process.env.REACT_APP_IMAGE_URL;

export default function Categories() {
  const { success, error, clearToasts } = useToastNotifications();
  const [categories, setCategories] = useState<Category[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Sorting functionality

  const [sortField, setSortField] = useState<'name' | 'status' | 'createdAt' | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    description: '',
    status: 'active',
    parentId: null,
    image: null,
    imageRemove: false,
  });
  const [originalData, setOriginalData] = useState<Category | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // FIX: These must be declared using useState
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const options = [
    { label: 'All Status', value: '' },
    { label: 'Active', value: 'active', color: 'text-green-600' },
    { label: 'Inactive', value: 'inactive', color: 'text-red-600' },
  ];

  const fetchCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      const shortBy = sortField || undefined;
      const shortType = sortOrder || undefined;
      const response = await realAPI.categories.getList(
        search,
        page,
        limit,
        statusFilter,
        shortBy,
        shortType
      );

      // Handle case where checkInvalidToken returns undefined
      if (!response) {
        setCategories([]);
        setTotal(0);
        return;
      }

      if (response.error === 'not_found') {
        setCategories([]);
        setTotal(0);
      } else {
        // Map the API response to match the expected Category type
        const normalized = (response.data || []).map((item: any) => ({
          id: String(item.id),
          name: item.name || 'Unnamed Category',
          description: item.description || '',
          status: item.status || 'active',
          image: item.image || null,
          parentId: item.parentId || null,
          parentCategoryName: item.parentCategoryName || null,
          createdAt: item.createdAt || new Date().toISOString(),
          updatedAt: item.updatedAt || new Date().toISOString(),
        }));
        setCategories(normalized);
        setTotal(response.total || 0);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Set empty state on error
      setCategories([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [search, page, limit, statusFilter, sortField, sortOrder]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Update searchInput immediately as user types
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  // Debounce the actual search to reduce API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (editingId && formData.parentId && formData.parentId === editingId) {
      errors.parentId = 'A category cannot be its own parent';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddClick = () => {
    setFormData({
      name: '',
      description: '',
      status: 'active',
      parentId: null,
      image: null,
      imageRemove: false,
    });
    setFormErrors({});
    setUploadedImage(null);
    setIsAddModalOpen(true);
  };

  const handleEditClick = (category: Category) => {
    setOriginalData(category);
    // Prepare data structure
    const categoryData: CategoryFormData = {
      name: category.name,
      description: category.description || '',
      status: category.status,
      // parentId must be string or null for frontend form input
      parentId: category.parentId !== null ? String(category.parentId) : null,
      image: category.image, // S3 key
      imageRemove: false,
    };

    // Set current form data
    setFormData(categoryData);
    setEditingId(category.id);
    setFormErrors({});

    // Set image preview URL
    setUploadedImage(category.image ? `${IMAGE_URL}${category.image}` : null);
    setIsEditModalOpen(true);
  };

  // Helper to convert form strings to API numbers/nulls, specifically for partial updates
  const buildPayloadForApi = (payload: Partial<CategoryFormData>) => {
    const apiPayload: Record<string, any> = { ...payload };

    // Convert parentId if it exists in the payload
    if (payload.parentId !== undefined) {
      apiPayload.parentId = payload.parentId ? Number(payload.parentId) : null;
    }

    return apiPayload;
  };

  const handleAddSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // For ADD, we send the entire formData
      const apiPayload = buildPayloadForApi(formData as CategoryFormData);

      // FIX: Cast to 'any' to satisfy the TypeScript requirement for createCategory
      await realAPI.categories.createCategory(apiPayload as any);

      await fetchCategories();
      setIsAddModalOpen(false);
      setFormData({
        name: '',
        description: '',
        status: 'active',
        parentId: null,
        image: null,
        imageRemove: false,
      });
      setUploadedImage(null);
      success('Category created successfully', 'New category has been added');
    } catch (err) {
      console.error('Failed to create category:', err);
      error('Failed to create category', 'Please try again later');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!validateForm() || !editingId || !originalData) return;

    setIsSubmitting(true);
    try {
      const payload: Partial<CategoryFormData> = {};

      if (formData.name !== originalData.name) payload.name = formData.name;
      if (formData.description !== (originalData.description || ''))
        payload.description = formData.description;
      if (formData.status !== originalData.status) payload.status = formData.status;

      const originalParentId =
        originalData.parentId !== null ? String(originalData.parentId) : null;
      if (formData.parentId !== originalParentId) {
        payload.parentId = formData.parentId;
      }

      if (formData.imageRemove) {
        payload.imageRemove = true;
      } else if (formData.image !== originalData.image) {
        payload.image = formData.image;
      }

      if (Object.keys(payload).length === 0) {
        success('No changes detected', 'Category remains the same');
        setIsEditModalOpen(false);
        setEditingId(null);
        setUploadedImage(null);
        setIsSubmitting(false);
        return;
      }

      // Show immediate success feedback
      success('Category updated successfully', 'Category has been updated');

      // The API call is made with the partial payload
      const apiPayload = buildPayloadForApi(payload);
      await realAPI.categories.updateCategory(editingId, apiPayload);

      await fetchCategories();
      setIsEditModalOpen(false);
      setEditingId(null);
      setUploadedImage(null);
    } catch (err: any) {
      console.error('Failed to update category:', err);
      error('Failed to update category', 'Please try again later');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = (category: Category) => {
    const newStatus = category.status === 'active' ? 'inactive' : 'active';

    if (newStatus === 'inactive') {
      // Clear previous popups/toasts immediately so only latest status popup is visible
      setIsAddModalOpen(false);
      setIsEditModalOpen(false);
      setIsDeleteModalOpen(false);
      setIsPreviewModalOpen(false);
      setCategoryToDelete(null);
      setEditingId(null);
      setUploadedImage(null);
      clearToasts();
    }

    // 1. Show immediate success feedback
    success(`Category status updated to ${newStatus}`, 'Status changed successfully');

    // 2. Update UI instantly
    setCategories((prev) =>
      prev.map((cat) => (cat.id === category.id ? { ...cat, status: newStatus } : cat))
    );

    // 3. Background API call (silent)
    realAPI.categories.updateCategory(category.id, { status: newStatus }).catch((err: any) => {
      console.error('Failed to update category:', err);
      error('Failed to update status on server', 'The change has been reverted');

      // 4. Revert UI on error
      setCategories((prev) =>
        prev.map((cat) =>
          cat.id === category.id
            ? { ...cat, status: category.status as 'active' | 'inactive' }
            : cat
        )
      );
    });
  };

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    setIsDeleting(true);

    try {
      await realAPI.categories.deleteCategory(categoryToDelete.id);
      if (categories.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        await fetchCategories();
      }
      setIsDeleteModalOpen(false);
      setCategoryToDelete(null);
      success('Category deleted successfully', 'Category has been removed');
    } catch (err: any) {
      console.error('Failed to delete category:', err);
      error('Failed to delete category', 'Please try again later');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateImageFile(file);
    if (validationError) {
      // alert(validationError);
      // Replace alert with toast
      error(validationError, 'Invalid image file');
      return;
    }

    setIsUploading(true);

    try {
      const result = await uploadImage(file);
      // 1. Set the full S3 URL for local preview display
      setUploadedImage(result.url);

      setFormData({
        ...formData,
        image: result.fileName, // Set the new S3 key in the form data
      });
      success('Image uploaded successfully', 'Image is ready to be saved');
    } catch (err: any) {
      console.error('Image upload failed:', err);
      // alert("Failed to upload image. Please try again.");
      error('Failed to upload image', 'Please try again');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    // Clear preview image
    setUploadedImage(null);

    // Clear image key from form data and set imageRemove flag
    setFormData({
      ...formData,
      image: null,
      imageRemove: true,
    });
  };

  // Handler for image preview
  const handleImagePreview = (imageKey: string | null | undefined) => {
    if (imageKey) {
      setPreviewImageUrl(`${IMAGE_URL}${imageKey}`);
      setIsPreviewModalOpen(true);
    }
  };

  // Helper to get the currently edited category for modal title
  const currentCategory = editingId ? categories.find((c) => c.id === editingId) : null;

  // Table shimmer component
  const TableShimmer = () => (
    <div className="table-container">
      <div className="table-min-w">
        <div className="grid grid-cols-8 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
          <div className="text-gray-700 dark:text-white col-span-1" style={{ paddingLeft: '20px' }}>
            Image
          </div>
          <div className="text-gray-700 dark:text-white col-span-3" style={{ paddingLeft: '15px' }}>
            Name
          </div>
          <div className="text-gray-700 dark:text-white col-span-2" style={{ paddingLeft: '19px' }}>
            Created At
          </div>
          <div className="text-gray-700 dark:text-white col-span-1" style={{ paddingLeft: '16px' }}>
            Status
          </div>
          <div className="text-gray-700 dark:text-white text-center col-span-1">Actions</div>
        </div>

        <div className="flex flex-col items-center justify-center py-12">
          <div className="loader"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="overflow-hidden">
      {/* Header */}
      <Card className="mb-2 p-[10px]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          {/* Title + Add Button */}
          <div className="flex items-center gap-6 mb-4 sm:mb-0">
            <h1 className="text-2xl font-bold text-gray-900 whitespace-nowrap">
              Categories Management
            </h1>
            <button
              onClick={handleAddClick}
              className="w-8 h-8 flex items-center justify-center bg-primary-500 text-white rounded-full 
        hover:bg-primary-600 transition-colors text-2xl font-extrabold leading-none"
              title="Add Category"
            >
              +
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap lg:items-center lg:justify-end gap-3 w-full">
            {/* Total Categories */}
            <div className="flex items-center">
              <div
                className="flex items-center gap-3 bg-gray-100 px-6 h-10
          rounded-xl text-gray-800 text-base font-medium shadow-sm w-full sm:w-auto justify-center"
              >
                <span>Total Categories: {total}</span>
              </div>
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-64 h-10">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full h-full pl-10 pr-4 border border-gray-300 rounded-lg 
          focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Status Filter */}
            <div
              className={`relative flex-shrink-0 transition-all duration-200
        ${!statusFilter ? 'w-[140px]' : 'w-[162px]'}`}
            >
              <Listbox
                value={statusFilter}
                onChange={(v) => {
                  setStatusFilter(v);
                  setPage(1);
                }}
              >
                <Listbox.Button
                  className="w-full h-10 pl-4 pr-10 text-left bg-white border border-gray-300
            rounded-lg shadow-sm focus:outline-none focus:ring-2 
            focus:ring-primary-500 transition-all duration-200 hover:border-gray-400 
            cursor-pointer flex items-center overflow-hidden"
                >
                  <span className="block truncate">
                    {options.find((o) => o.value === statusFilter)?.label || 'Status'}
                  </span>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </Listbox.Button>

                <Listbox.Options className="absolute z-20 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200">
                  {options.map((option) => (
                    <Listbox.Option
                      key={option.value}
                      value={option.value}
                      className={({ active }) =>
                        `px-4 py-2 cursor-pointer transition-colors ${active ? 'bg-gray-100' : ''}`
                      }
                    >
                      <span className={option.color}>{option.label}</span>
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Listbox>
            </div>

            {/* Reset Filters */}
            <Button
              variant="secondary"
              onClick={() => {
                setStatusFilter('');
                setSortField(null);
                setSortOrder(null);
                setSearch('');
                setSearchInput('');
                setPage(1);
                success('All filters reset', 'Filters have been cleared');
              }}
              className="px-4 h-10"
              style={{ width: '162px' }}
            >
              Reset Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Categories Table */}
      <Card>
        {isLoading ? (
          <TableShimmer />
        ) : categories.length === 0 ? (
          <div className="table-container">
            <div className="table-min-w">
              {/* Grid Header */}
              <div
                className="grid grid-cols-8 gap-3 p-4 rounded-t-xl font-semibold text-gray-700 text-[15px]
          border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100
          dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm"
              >
                <div className="col-span-1 pl-3">Image</div>
                <div className="col-span-3 pl-2">Name</div>
                <div className="col-span-2">Created At</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-1 text-center">Actions</div>
              </div>

              {/* Empty State */}
              <div className="text-center py-12 bg-gray-50 rounded-b-xl">
                <img src="/NoRecord.svg" alt="No records found" className="mx-auto mb-4" />
                <Button onClick={handleAddClick}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Category
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="table-container">
              <div className="table-min-w">
                {/* Grid Header */}
                <div
                  className="grid grid-cols-8 gap-3 p-4 rounded-t-xl font-semibold text-gray-700 text-[15px]
          border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100
          dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm"
                >
                  <div className="col-span-1 pl-3">Image</div>

                  <div
                    className="col-span-3 pl-2 cursor-pointer hover:text-primary-600"
                    onClick={() => {
                      setSortField('name');
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      setPage(1);
                    }}
                  >
                    Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>

                  <div
                    className="col-span-2 cursor-pointer hover:text-primary-600"
                    onClick={() => {
                      setSortField('createdAt');
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      setPage(1);
                    }}
                  >
                    Created At {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>

                  <div
                    className="col-span-1 cursor-pointer hover:text-primary-600"
                    onClick={() => {
                      setSortField('status');
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      setPage(1);
                    }}
                  >
                    Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>

                  <div className="col-span-1 text-center">Actions</div>
                </div>

                {/* Grid Body */}
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {categories.map((category) => (
                    <div
                      key={category.id}
                      className="grid grid-cols-8 gap-3 p-2.5 items-center
                hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      {/* Image */}
                      <div className="col-span-1">
                        <div
                          className="w-12 h-10 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center cursor-pointer"
                          onClick={() => category.image && handleImagePreview(category.image)}
                        >
                          <img
                            src={category.image ? `${IMAGE_URL}${category.image}` : '/service.png'}
                            alt={category.name}
                            className="w-full h-full object-cover"
                            onError={(e) => (e.currentTarget.src = '/service.png')}
                          />
                        </div>
                      </div>

                      {/* Name */}
                      <div className="col-span-3 font-medium dark:text-white truncate">
                        <div className="flex items-center gap-2">
                          {category.parentId && (
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-500">
                              Subcategory
                            </span>
                          )}
                          <span className="truncate">{category.name}</span>
                        </div>
                      </div>

                      {/* Created */}
                      <div className="col-span-2 text-sm text-black-600 dark:text-gray-300">
                        {formatDate(category.createdAt)}
                      </div>

                      {/* Status */}
                      <div className="col-span-1">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize
                    ${
                      category.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                        >
                          {category.status}
                        </span>
                      </div>

                      {/* Actions ✅ perfectly aligned */}
                      <div className="col-span-1 flex justify-center gap-3">
                        <button
                          onClick={() => handleToggleStatus(category)}
                          className={`p-2 rounded-lg ${
                            category.status === 'active'
                              ? 'text-green-600 hover:bg-green-50'
                              : 'text-red-600 hover:bg-red-50'
                          }`}
                        >
                          {category.status === 'active' ? (
                            <ToggleRight className="w-6 h-6" />
                          ) : (
                            <ToggleLeft className="w-6 h-6" />
                          )}
                        </button>

                        <button
                          onClick={() => handleEditClick(category)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                          <Edit2 className="w-6 h-6" />
                        </button>

                        <button
                          onClick={() => handleDeleteClick(category)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-6 h-6" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}{' '}
                categories
              </div>

              <div className="flex items-center gap-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>

                <span className="text-sm font-medium">
                  Page {page} of {Math.ceil(total / limit) || 1}
                </span>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= Math.ceil(total / limit)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Add Category Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Category"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={formErrors.name}
            placeholder="Enter category name"
          />

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category Image</label>
            <div className="mt-1 flex items-center space-x-4">
              {uploadedImage ? (
                <div className="relative">
                  <img
                    src={uploadedImage || '/service.png'}
                    alt="Category preview"
                    className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="h-20 w-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                  <Upload className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="category-image-upload"
                />
                <label
                  htmlFor="category-image-upload"
                  className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {isUploading ? 'Uploading...' : uploadedImage ? 'Change Image' : 'Upload Image'}
                </label>
                <p className="text-xs text-gray-500 mt-1">JPEG, PNG, GIF, WebP up to 5MB</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <Button variant="secondary" onClick={() => setIsAddModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSubmit} loading={isSubmitting}>
              Add Category
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Category Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title={`Edit Category: ${currentCategory?.name || ''}`}
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={formErrors.name}
            placeholder="Enter category name"
          />

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category Image</label>
            <div className="mt-1 flex items-center space-x-4">
              {uploadedImage ? (
                <div className="relative">
                  <img
                    src={uploadedImage || '/service.png'}
                    alt="Category preview"
                    className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="h-20 w-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                  <Upload className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="category-image-edit"
                />
                <label
                  htmlFor="category-image-edit"
                  className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {isUploading ? 'Uploading...' : uploadedImage ? 'Change Image' : 'Upload Image'}
                </label>
                <p className="text-xs text-gray-500 mt-1">JPEG, PNG, GIF, WebP up to 5MB</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} loading={isSubmitting}>
              Update Category
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Category"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete this category? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* --- Image Preview Modal --- */}
      <Modal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        title="Image Preview"
        size="lg"
      >
        <div className="flex justify-center items-center p-4">
          {previewImageUrl ? (
            <img
              src={previewImageUrl}
              alt="Full Service Preview"
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-xl"
            />
          ) : (
            <p className="text-gray-500">No image available.</p>
          )}
        </div>
        <div className="flex justify-end mt-4">
          <Button variant="secondary" onClick={() => setIsPreviewModalOpen(false)}>
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
}
